/**
 * TextResponseAccordion Component
 * Renders free-form text survey responses as a collapsible accordion.
 * Collapsed: chevron + question text + response count
 * Expanded: individual responses as quote cards, never with a name
 * (survey results are anonymous for every role, MED-1345)
 */

import { useState } from 'react';
import { ChevronRight } from 'react-feather';
import type { TextResponse } from '../../../services/api/feedbackSurveys';
import styles from './TextResponseAccordion.module.css';

interface TextResponseAccordionProps {
  questionText: string;
  responses: TextResponse[];
  defaultExpanded?: boolean;
}

export function TextResponseAccordion({
  questionText,
  responses,
  defaultExpanded = false,
}: TextResponseAccordionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const responseLabel = responses.length === 1 ? 'response' : 'responses';

  return (
    <div className={styles.container}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <ChevronRight
          size={16}
          className={`${styles.chevron} ${expanded ? styles.chevronExpanded : ''}`}
        />
        <span className={styles.questionText}>{questionText}</span>
        <span className={styles.responseCount}>
          {responses.length} {responseLabel}
        </span>
      </button>

      {expanded && (
        <div className={styles.responseList}>
          {responses.map((response, index) => (
            <div key={index} className={styles.responseItem}>
              <span className={styles.responseQuote}>{response.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TextResponseAccordion;
